import { Badge } from "@/components/base/badges/badges";

/**
 * Status for creators within a collaboration, based on their progress
 * in the collaboration funnel (invitation -> sample -> post -> GMV)
 */
export type CreatorCollaborationStatus =
  | "invited"
  | "joined"
  | "sample_requested"
  | "sample_in_transit"
  | "pending_post"
  | "posted_video"
  | "cancelled";

type Props = {
  sampleApplicationStatus: string | null;
  collaborationType?: "target" | "open";
};

/**
 * Sample application statuses grouped by tab category
 */
const SAMPLE_STATUS_STATES = ["AWAITING_SHIPMENT", "SHIPPED"];
const PENDING_POST_STATES = ["CONTENT_PENDING"];
const POSTED_VIDEO_STATES = ["COMPLETED", "OPS_COMPLETED"];
const CANCELLED_STATES = ["WITHDRAW_CANCELLED", "UNFULFILL_CANCELLED", "OPS_CANCELLED", "OPS_FAILED"];

/**
 * Derives the display status from the sample application status
 */
const deriveDisplayStatus = (
  sampleApplicationStatus: string | null,
  collaborationType: "target" | "open" = "target",
): CreatorCollaborationStatus => {
  if (!sampleApplicationStatus) {
    return collaborationType === "open" ? "joined" : "invited";
  }

  if (SAMPLE_STATUS_STATES.includes(sampleApplicationStatus)) {
    return "sample_in_transit";
  }

  if (PENDING_POST_STATES.includes(sampleApplicationStatus)) {
    return "pending_post";
  }

  if (POSTED_VIDEO_STATES.includes(sampleApplicationStatus)) {
    return "posted_video";
  }

  if (CANCELLED_STATES.includes(sampleApplicationStatus)) {
    return "cancelled";
  }

  // For other statuses like PENDING, REJECT_CANCELLED, OVERDUE_CANCELLED, etc.
  return "invited";
};

/**
 * Maps status values to user-friendly display labels
 */
const STATUS_LABELS: Record<CreatorCollaborationStatus, string> = {
  invited: "Invited",
  joined: "Joined",
  sample_requested: "Sample Requested",
  sample_in_transit: "Sample in Transit",
  pending_post: "Pending Post",
  posted_video: "Posted Video",
  cancelled: "Cancelled",
};

/**
 * Maps status values to badge colors based on the reference image
 */
const STATUS_COLORS: Record<
  CreatorCollaborationStatus,
  "warning" | "success" | "gray" | "error" | "blue-light" | "orange"
> = {
  invited: "gray",
  joined: "gray",
  sample_requested: "blue-light",
  sample_in_transit: "blue-light",
  pending_post: "orange",
  posted_video: "success",
  cancelled: "error",
};

/**
 * Badge component for displaying creator collaboration status.
 * Derives the display status from the sample application status.
 * Used in the collaboration detail page to show the status of each creator.
 */
export const CreatorInvitationStatusBadge = ({ sampleApplicationStatus, collaborationType }: Props) => {
  const displayStatus = deriveDisplayStatus(sampleApplicationStatus, collaborationType);

  return (
    <Badge color={STATUS_COLORS[displayStatus]} size="sm">
      {STATUS_LABELS[displayStatus]}
    </Badge>
  );
};
